// ProviderDashboard.js - Caretaker dashboard with tab navigation
// Tabs: Available Jobs, My Jobs, Earnings, Messages, My Profile
// Includes clickable profile photo, earnings tracking, and rating display

import React, { useEffect, useState } from 'react';
import emailjs from '@emailjs/browser';
import { auth, db, storage } from '../firebase';
import { signOut, EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  addDoc,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL
} from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import Messages from './Messages';
import NotificationBell from './NotificationBell';

// Safely extract a numeric amount from a booking document.
// Tries multiple field names, strips currency symbols/whitespace, and returns 0
// instead of NaN for any missing or malformed value (covers old Gravemark docs).
// Supply requirements per add-on — drives the claim confirmation modal
const SUPPLY_REQUIREMENTS = {
  base:             { label: 'Base Visit — Headstone Cleaning', supplies: ['Cleaning solution', 'Soft brush', 'Microfiber cloths', 'Water source or jug'], purchase: false, budget: null },
  bouquet:          { label: 'Fresh Cut Bouquet',               supplies: ['Fresh cut flowers'],                                                             purchase: true,  budget: 12,  note: 'Purchase fresh cut bouquet before arrival. You will be reimbursed through your payout.' },
  planted_flowers:  { label: 'Planted Seasonal Flowers',        supplies: ['Seasonal flowers', 'Small trowel or hand shovel'],                               purchase: true,  budget: 20,  note: 'Purchase seasonal flowers before arrival. You will be reimbursed through your payout.' },
  flowers_refresh:  { label: 'Flower Bed Refresh',              supplies: ['Bag of mulch', 'Gloves', 'Small rake'],                                          purchase: true,  budget: 8,   note: 'Purchase mulch before arrival. You will be reimbursed through your payout.' },
  flowers_bundle:   { label: 'Flowers + Refresh Bundle',        supplies: ['Seasonal flowers', 'Bag of mulch', 'Small trowel', 'Gloves', 'Small rake'],      purchase: true,  budget: 25,  note: 'Purchase flowers and mulch before arrival. You will be reimbursed through your payout.' },
  seasonal_decor:   { label: 'Seasonal Decoration',             supplies: ['Wreath or yard stakes'],                                                         purchase: true,  budget: 12,  note: 'Purchase seasonal decoration before arrival. You will be reimbursed through your payout.' },
  birthday:         { label: 'Birthday Remembrance Setup',      supplies: ['Balloon stakes', 'Small bouquet'],                                               purchase: true,  budget: 10,  note: 'Purchase balloon stakes and flowers before arrival. You will be reimbursed through your payout.' },
  bow:              { label: 'Bow / Ribbon',                    supplies: ['Decorative bow or ribbon'],                                                      purchase: true,  budget: 3,   note: 'Purchase a decorative bow before arrival. You will be reimbursed through your payout.' },
  solar_light:      { label: 'Solar Memorial Light',            supplies: ['Solar memorial light'],                                                          purchase: true,  budget: 8,   note: 'Purchase a solar light before arrival. You will be reimbursed through your payout.' },
  flag_small:       { label: 'Veteran / Holiday Flag',          supplies: ['Small flag'],                                                                    purchase: true,  budget: 3,   note: 'Purchase a small flag before arrival. You will be reimbursed through your payout.' },
  flag_full:        { label: 'Full Flag Kit',                   supplies: ['Full flag display kit'],                                                         purchase: true,  budget: 10,  note: 'Purchase a full flag kit before arrival. You will be reimbursed through your payout.' },
  overgrowth:       { label: 'Heavy Overgrowth Clearing',       supplies: ['Gloves', 'Hand pruners', 'Small rake', 'Trash bags'],                           purchase: false, budget: null },
  extra_grave:      { label: 'Additional Grave',                supplies: ['Cleaning solution', 'Soft brush', 'Microfiber cloths', 'Water source or jug'], purchase: false, budget: null },
  extended:         { label: 'Extended Visit',                  supplies: [],                                                                                purchase: false, budget: null, note: 'No extra supplies needed — budget extra time for this visit.' },
  video:            { label: 'Video Update',                    supplies: ['Phone with camera', 'Good lighting'],                                           purchase: false, budget: null, note: 'Record a 30-60 second walkthrough video of the completed visit.' },
  report:           { label: 'Written Visit Report',            supplies: ['Notes app or paper'],                                                            purchase: false, budget: null, note: 'Document your observations and write a detailed report of the visit.' },
};

const getBookingAmount = (booking) => {
  const raw = booking.price ?? booking.amount ?? booking.totalAmount ?? booking.total ?? 0;
  const parsed = parseFloat(String(raw).replace(/[^0-9.]/g, ''));
  return isNaN(parsed) ? 0 : parsed;
};

function ProviderDashboard() {
  const navigate = useNavigate();

  // State for provider data, jobs, loading, active tab, and uploading status
  const [providerData, setProviderData] = useState(null);
  const [availableJobs, setAvailableJobs] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('available');
  const [expandedJob, setExpandedJob] = useState(null);
  const [uploadingJobId, setUploadingJobId] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Flower allowance modal — holds the job object that triggered the intercept
  const [claimConfirmJob, setClaimConfirmJob] = useState(null);

  // Change password modal state
  const [showChangePw, setShowChangePw] = useState(false);
  const [changePwForm, setChangePwForm] = useState({ current: '', next: '', confirm: '' });
  const [changePwError, setChangePwError] = useState('');
  const [changePwSuccess, setChangePwSuccess] = useState(false);
  const [changePwLoading, setChangePwLoading] = useState(false);

  // Error state for Firestore fetch failures
  const [fetchError, setFetchError] = useState(false);

  // Feature 2 — bio editing
  const [editingBio, setEditingBio] = useState(false);
  const [bioValue, setBioValue] = useState('');

  // Feature 3 — edit profile
  const [editingProfile, setEditingProfile] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editServiceCity, setEditServiceCity] = useState('');
  const [editServiceState, setEditServiceState] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState(false);

  useEffect(() => {
    // onAuthStateChanged waits for Firebase to restore session on refresh
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        // Fetch provider profile from Firestore
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!userDoc.exists()) {
          navigate('/login');
          return;
        }

        const provider = userDoc.data();

        // Make sure this user is a provider not a customer
        if (provider.role !== 'provider') {
          navigate('/dashboard');
          return;
        }

        // If onboarding isn't done yet, send them there first
        if (!provider.onboardingComplete) {
          navigate('/provider/onboarding');
          return;
        }

        // If they haven't acknowledged Terms of Service, gate before the agreement
        // (flow: onboarding → terms → agreement → handbook → dashboard)
        if (!provider.termsAcknowledged) {
          navigate('/terms-acknowledge-provider');
          return;
        }

        // If they haven't signed the contractor agreement, send them there next
        if (!provider.contractorAgreementSigned) {
          navigate('/provider/agreement');
          return;
        }

        // If they haven't acknowledged the handbook, send them there last
        // (must come after the agreement check — agreement → handbook → dashboard)
        if (!provider.handbookAcknowledged) {
          navigate('/provider/handbook');
          return;
        }

        setProviderData(provider);

        // Initialize editable state for bio and profile
        setBioValue(provider.bio || '');
        setEditFirstName(provider.firstName || '');
        setEditLastName(provider.lastName || '');
        setEditPhone(provider.phone || '');
        setEditServiceCity(provider.serviceCity || '');
        setEditServiceState(provider.serviceState || '');

        // Fetch available jobs scoped to this provider's city and state.
        // Filtering happens in Firestore — no provider sees jobs outside their area.
        //
        // REQUIRED FIRESTORE COMPOSITE INDEX (create once in Firebase Console):
        //   Firebase Console → Firestore → Indexes → Composite → Add index
        //   Collection: bookings
        //   Fields:     status       Ascending
        //               cemeteryState Ascending
        //               cemeteryCity  Ascending
        //   Query scope: Collection
        //
        // Without this index Firestore will throw an error and include a direct
        // link in the browser console to create it automatically — click that link.
        const availableQuery = query(
          collection(db, 'bookings'),
          where('status', '==', 'pending'),
          where('cemeteryState', '==', provider.serviceState),
          where('cemeteryCity', '==', provider.serviceCity)
        );

        const availableSnapshot = await getDocs(availableQuery);
        const availableList = availableSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAvailableJobs(availableList);

        // Notify provider of new available jobs they haven't been notified about yet
        // Uses localStorage to avoid duplicate notifications on re-render
        const notifiedJobs = JSON.parse(localStorage.getItem('gravemark_notified_jobs') || '[]');
        for (const job of availableList) {
          if (!notifiedJobs.includes(job.id)) {
            try {
              await addDoc(collection(db, 'notifications'), {
                userId: user.uid,
                message: `New job available in ${job.cemeteryCity.charAt(0).toUpperCase() + job.cemeteryCity.slice(1)}`,
                type: 'new_job',
                read: false,
                createdAt: serverTimestamp(),
              });
            } catch (notifErr) {
              console.error('Error creating job notification:', notifErr);
            }
            notifiedJobs.push(job.id);
          }
        }
        localStorage.setItem('gravemark_notified_jobs', JSON.stringify(notifiedJobs));

        // Fetch jobs this provider has claimed
        const myJobsQuery = query(
          collection(db, 'bookings'),
          where('providerId', '==', user.uid)
        );

        const myJobsSnapshot = await getDocs(myJobsQuery);
        const myJobsList = myJobsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setMyJobs(myJobsList);

      } catch (err) {
        console.error('Error fetching provider data:', err);
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for unread messages across all conversations
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', user.uid)
    );

    const unsubscribe = onSnapshot(q, async (convSnap) => {
      let total = 0;
      for (const convDoc of convSnap.docs) {
        const msgsSnap = await getDocs(
          query(
            collection(db, 'conversations', convDoc.id, 'messages'),
            where('senderId', '!=', user.uid)
          )
        );
        const unread = msgsSnap.docs.filter(msgDoc => {
          const data = msgDoc.data();
          return !(data.readBy || []).includes(user.uid);
        }).length;
        total += unread;
      }
      setUnreadCount(total);
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Called when provider clicks Claim Job
  const handleClaimJob = async (jobId) => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      // Write assigned status + provider details (including bio and rating for customer card display)
      await updateDoc(doc(db, 'bookings', jobId), {
        status: 'assigned',
        providerId: user.uid,
        providerName: `${providerData.firstName} ${providerData.lastName}`,
        providerPhotoUrl: providerData.photoUrl || null,
        providerRating: providerData.rating || null,
        providerBio: providerData.bio || null,
        claimedAt: new Date(),
      });

      const claimedJob = availableJobs.find(job => job.id === jobId);
      if (claimedJob) {
        const updatedJob = {
          ...claimedJob,
          status: 'assigned',
          providerId: user.uid,
          providerRating: providerData.rating || null,
          providerBio: providerData.bio || null,
        };
        setMyJobs(prev => [...prev, updatedJob]);
        setAvailableJobs(prev => prev.filter(job => job.id !== jobId));

        // Notify the customer that their caretaker has been assigned
        try {
          await addDoc(collection(db, 'notifications'), {
            userId: claimedJob.customerId,
            message: `Your caretaker has been assigned for ${claimedJob.lovedOneName}`,
            type: 'assigned',
            read: false,
            createdAt: serverTimestamp(),
          });
        } catch (notifErr) {
          console.error('Error creating assignment notification:', notifErr);
        }

        // Create or find conversation and post system assignment message
        try {
          const existingConvSnap = await getDocs(
            query(collection(db, 'conversations'), where('bookingId', '==', jobId))
          );

          let convId;
          if (existingConvSnap.empty) {
            // Look up customer's name so the conversation shows correctly on their end
            const customerDoc = await getDoc(doc(db, 'users', claimedJob.customerId));
            const customerData = customerDoc.exists() ? customerDoc.data() : null;
            const customerName = customerData
              ? `${customerData.firstName} ${customerData.lastName}`
              : 'Customer';

            const convRef = await addDoc(collection(db, 'conversations'), {
              bookingId: jobId,
              participants: [claimedJob.customerId, user.uid],
              customerId: claimedJob.customerId,
              customerName: customerName,
              providerId: user.uid,
              providerName: `${providerData.firstName} ${providerData.lastName}`,
              lovedOneName: claimedJob.lovedOneName,
              lastMessage: 'Caretaker assigned',
              lastMessageAt: serverTimestamp(),
              createdAt: serverTimestamp(),
            });
            convId = convRef.id;
          } else {
            convId = existingConvSnap.docs[0].id;
          }

          // Post system message announcing assignment
          await addDoc(collection(db, 'conversations', convId, 'messages'), {
            text: `🌿 ${providerData.firstName} ${providerData.lastName} has been assigned as your caretaker. Feel free to message them here with any questions or special requests.`,
            senderId: user.uid,
            senderName: `${providerData.firstName} ${providerData.lastName}`,
            senderRole: 'provider',
            type: 'system',
            readBy: [user.uid],
            createdAt: serverTimestamp(),
          });

          await updateDoc(doc(db, 'conversations', convId), {
            lastMessage: 'Caretaker assigned',
            lastMessageAt: serverTimestamp(),
          });
        } catch (convErr) {
          console.error('Error creating assignment conversation:', convErr);
        }

        // Send caretaker assigned email to customer — fire and forget
        getDoc(doc(db, 'users', claimedJob.customerId)).then(customerDoc => {
          const customerData = customerDoc.exists() ? customerDoc.data() : {};
          emailjs.send(
            process.env.REACT_APP_EMAILJS_SERVICE_ID,
            process.env.REACT_APP_EMAILJS_TEMPLATE_ASSIGNED,
            {
              customer_name: customerData.firstName
                ? `${customerData.firstName} ${customerData.lastName || ''}`.trim()
                : 'Valued Customer',
              service_type: 'Grave Visit',
              cemetery_name: claimedJob.cemeteryName || '',
              caretaker_name: providerData
                ? `${providerData.firstName} ${providerData.lastName}`
                : 'Your Caretaker',
              to_email: customerData.email || '',
            },
            process.env.REACT_APP_EMAILJS_PUBLIC_KEY
          ).catch(err => {
            console.error('EmailJS caretaker assigned error:', err);
          });
        }).catch(err => {
          console.error('EmailJS: could not fetch customer data:', err);
        });
      }

      setActiveTab('myjobs');

    } catch (err) {
      console.error('Error claiming job:', err);
    }
  };

  // Called when provider uploads a before or after photo for a job
  const handlePhotoUpload = async (jobId, photoType, file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (JPG or PNG).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('Photo must be under 10 MB.');
      return;
    }

    try {
      setUploadingJobId(jobId);

      const photoRef = ref(storage, `photos/${jobId}/${photoType}_${Date.now()}.jpg`);
      await uploadBytes(photoRef, file);
      const downloadURL = await getDownloadURL(photoRef);

      await updateDoc(doc(db, 'bookings', jobId), {
        [`${photoType}PhotoUrl`]: downloadURL,
        [`${photoType}PhotoUploadedAt`]: new Date(),
      });

      setMyJobs(prev => prev.map(job => {
        if (job.id === jobId) {
          return { ...job, [`${photoType}PhotoUrl`]: downloadURL };
        }
        return job;
      }));

      const job = myJobs.find(j => j.id === jobId);
      const hasBeforePhoto = photoType === 'before' ? true : job?.beforePhotoUrl;
      const hasAfterPhoto = photoType === 'after' ? true : job?.afterPhotoUrl;

      if (hasBeforePhoto && hasAfterPhoto) {
        await updateDoc(doc(db, 'bookings', jobId), {
          status: 'completed',
          completedAt: new Date(),
        });

        setMyJobs(prev => prev.map(job =>
          job.id === jobId ? { ...job, status: 'completed' } : job
        ));

        // Notify the customer that their visit photos are ready
        const completedJobData = myJobs.find(j => j.id === jobId);
        if (completedJobData) {
          try {
            await addDoc(collection(db, 'notifications'), {
              userId: completedJobData.customerId,
              message: `Your visit photos are ready for ${completedJobData.lovedOneName}`,
              type: 'photos_ready',
              read: false,
              createdAt: serverTimestamp(),
            });
          } catch (notifErr) {
            console.error('Error creating photos notification:', notifErr);
          }
        }
      }

    } catch (err) {
      console.error('Error uploading photo:', err);
    } finally {
      setUploadingJobId(null);
    }
  };

  // Called when provider clicks their profile photo to change it
  // Clicking the photo triggers the hidden file input
  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (JPG or PNG).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('Photo must be under 10 MB.');
      return;
    }

    const user = auth.currentUser;
    if (!user) return;

    try {
      setUploadingPhoto(true);

      const photoRef = ref(storage, `profile-photos/${user.uid}`);
      await uploadBytes(photoRef, file);
      const downloadURL = await getDownloadURL(photoRef);

      await updateDoc(doc(db, 'users', user.uid), {
        photoUrl: downloadURL
      });

      setProviderData(prev => ({ ...prev, photoUrl: downloadURL }));

    } catch (err) {
      console.error('Error uploading profile photo:', err);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    localStorage.clear();
    navigate('/');
  };

  // Saves the updated bio to Firestore and updates local state
  const handleSaveBio = async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      await updateDoc(doc(db, 'users', user.uid), { bio: bioValue.trim() });
      setProviderData(prev => ({ ...prev, bio: bioValue.trim() }));
      setEditingBio(false);
    } catch (err) {
      console.error('Error saving bio:', err);
    }
  };

  // Password strength checks (used by change-password modal)
  const pwChecks = (pw) => ({
    length: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    number: /[0-9]/.test(pw),
    special: /[!@#$%^&*()\-_=+[\]{}|;:,.<>?]/.test(pw),
  });

  // Re-authenticates then sets a new password
  const handleChangePassword = async () => {
    setChangePwError('');
    const { current, next, confirm } = changePwForm;
    if (!current || !next || !confirm) { setChangePwError('All fields are required.'); return; }
    if (next !== confirm) { setChangePwError('New passwords do not match.'); return; }
    if (!Object.values(pwChecks(next)).every(Boolean)) {
      setChangePwError('New password does not meet strength requirements.'); return;
    }
    setChangePwLoading(true);
    try {
      const credential = EmailAuthProvider.credential(auth.currentUser.email, current);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, next);
      setChangePwSuccess(true);
      setChangePwForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setChangePwError('Current password is incorrect.');
      } else {
        setChangePwError('Failed to update password. Please try again.');
      }
    }
    setChangePwLoading(false);
  };

  // Saves edited profile fields (name, phone, service area) to Firestore
  const handleSaveProfile = async () => {
    const user = auth.currentUser;
    if (!user) return;
    if (!editFirstName.trim() || !editLastName.trim()) {
      setProfileError('Name fields cannot be empty.');
      return;
    }
    try {
      setProfileSaving(true);
      setProfileError('');
      await updateDoc(doc(db, 'users', user.uid), {
        firstName: editFirstName.trim(),
        lastName: editLastName.trim(),
        phone: editPhone.trim(),
        serviceCity: editServiceCity.toLowerCase().trim(),
        serviceState: editServiceState.toLowerCase().trim(),
      });
      setProviderData(prev => ({
        ...prev,
        firstName: editFirstName.trim(),
        lastName: editLastName.trim(),
        phone: editPhone.trim(),
        serviceCity: editServiceCity.toLowerCase().trim(),
        serviceState: editServiceState.toLowerCase().trim(),
      }));
      setEditingProfile(false);
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err) {
      setProfileError('Something went wrong. Please try again.');
    } finally {
      setProfileSaving(false);
    }
  };

  // ========== COMPUTED EARNINGS STATS ==========
  const completedJobs = myJobs.filter(j => j.status === 'completed').length;
  const activeJobs = myJobs.filter(j => j.status === 'assigned').length;
  const totalEarnings = myJobs
    .filter(j => j.status === 'completed')
    .reduce((total, job) => total + getBookingAmount(job) * 0.65, 0);

  const pendingEarnings = myJobs
    .filter(j => j.status === 'assigned')
    .reduce((total, job) => total + getBookingAmount(job) * 0.65, 0);

  // Helper to capitalize words
  const capitalize = (str) => {
    if (!str) return '';
    return str.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Helper to format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC'
    });
  };

  // Renders star display based on rating number
  const renderStars = (rating) => {
    const full = Math.floor(rating);
    const empty = 5 - full;
    return (
      <span>
        <span style={{ color: '#f59e0b' }}>{'★'.repeat(full)}</span>
        <span style={{ color: '#d1d5db' }}>{'★'.repeat(empty)}</span>
      </span>
    );
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard-header" style={{ padding: '32px 40px 0' }}>
          <div className="skeleton-line skeleton-line-medium" style={{ height: '28px', marginBottom: '8px' }} />
          <div className="skeleton-line skeleton-line-short" style={{ height: '16px' }} />
        </div>
        <div className="skeleton-stats-bar">
          {[1, 2, 3, 4].map(i => <div key={i} className="skeleton-stat" />)}
        </div>
        <div className="skeleton-tabs">
          {[1, 2, 3, 4, 5].map(i => <div key={i} className="skeleton-tab" />)}
        </div>
        <div className="skeleton-cards-wrapper">
          {[1, 2].map(i => (
            <div key={i} className="skeleton-card">
              <div style={{ flex: 1 }}>
                <div className="skeleton-line skeleton-line-short" style={{ height: '20px', marginBottom: '12px' }} />
                <div className="skeleton-line skeleton-line-medium" style={{ height: '14px', marginBottom: '8px' }} />
                <div className="skeleton-line skeleton-line-short" style={{ height: '14px' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                <div className="skeleton-line" style={{ width: '80px', height: '24px', borderRadius: '50px' }} />
                <div className="skeleton-line" style={{ width: '100px', height: '32px', borderRadius: '50px' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="dashboard">
        <div className="fetch-error-state">
          <div className="fetch-error-icon">⚠️</div>
          <p>Something went wrong. Please refresh and try again.</p>
          <button className="btn-primary" onClick={() => window.location.reload()}>Refresh</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">

      {/* ========== PROVIDER NAVBAR ========== */}
      <nav className="dashboard-nav">
        <a href="/" className="logo" style={{ textDecoration: 'none' }}>Evermark</a>
        <div className="dashboard-nav-right">
          {providerData && (
            <span className="dashboard-greeting">
              Hello, {providerData.firstName}
            </span>
          )}
          <NotificationBell />
          <button className="btn-outline" onClick={handleLogout}>
            Log Out
          </button>
        </div>
      </nav>

      {/* ========== TAB NAVIGATION ========== */}
      <div className="dashboard-tabs">
        <button
          className={`dashboard-tab ${activeTab === 'available' ? 'dashboard-tab-active' : ''}`}
          onClick={() => setActiveTab('available')}
        >
          🔍 Available Jobs {availableJobs.length > 0 && <span className="tab-badge">{availableJobs.length}</span>}
        </button>
        <button
          className={`dashboard-tab ${activeTab === 'myjobs' ? 'dashboard-tab-active' : ''}`}
          onClick={() => setActiveTab('myjobs')}
        >
          📋 My Jobs {activeJobs > 0 && <span className="tab-badge">{activeJobs}</span>}
        </button>
        <button
          className={`dashboard-tab ${activeTab === 'earnings' ? 'dashboard-tab-active' : ''}`}
          onClick={() => setActiveTab('earnings')}
        >
          💰 Earnings
        </button>
        <button
          className={`dashboard-tab ${activeTab === 'messages' ? 'dashboard-tab-active' : ''}`}
          onClick={() => { setActiveTab('messages'); setUnreadCount(0); }}
        >
          💬 Messages {unreadCount > 0 && <span className="tab-badge">{unreadCount}</span>}
        </button>
        <button
          className={`dashboard-tab ${activeTab === 'profile' ? 'dashboard-tab-active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          👤 My Profile
        </button>
      </div>

      <div className="dashboard-content">

        {/* ========== AVAILABLE JOBS TAB ========== */}
        {activeTab === 'available' && (
          <div>
            <div className="dashboard-header">
              <div>
                <h2>Available Jobs</h2>
                <p className="dashboard-subtitle">
                  Jobs near {capitalize(providerData?.serviceCity)},{' '}
                  {providerData?.serviceState?.toUpperCase()}
                </p>
              </div>
            </div>

            {/* Stats bar */}
            <div className="stats-bar">
              <div className="stat-card stat-card-green">
                <div className="stat-icon">💰</div>
                <div className="stat-info">
                  <span className="stat-number">${(totalEarnings / 100).toFixed(2)}</span>
                  <span className="stat-label">Total Earned</span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">✅</div>
                <div className="stat-info">
                  <span className="stat-number">{completedJobs}</span>
                  <span className="stat-label">Jobs Completed</span>
                </div>
              </div>
              <div className="stat-card stat-card-yellow">
                <div className="stat-icon">🌿</div>
                <div className="stat-info">
                  <span className="stat-number">{activeJobs}</span>
                  <span className="stat-label">Active Jobs</span>
                </div>
              </div>
              <div className="stat-card stat-card-message">
                <div className="stat-message-text">
                  {completedJobs > 0
                    ? `${completedJobs} famil${completedJobs > 1 ? 'ies' : 'y'} honored — thank you.`
                    : 'Claim your first job and start earning.'}
                </div>
              </div>
            </div>

            {/* Available jobs list — cards are collapsed by default, expand on click */}
            {availableJobs.length > 0 ? (
              <div className="bookings-list">
                {availableJobs.map(job => {
                  const isExpanded = expandedJob === job.id;
                  return (
                    <div key={job.id} className="booking-card-wrapper">
                      {/* Collapsed header — always visible */}
                      <div
                        className="job-card-header"
                        onClick={() => setExpandedJob(isExpanded ? null : job.id)}
                      >
                        <div className="job-card-header-left">
                          <span className="booking-badge">Grave Visit</span>
                          <h3>{job.lovedOneName}</h3>
                          <p className="job-card-location">
                            {capitalize(job.cemeteryCity)}, {job.cemeteryState?.toUpperCase()}
                          </p>
                          {/* Add-on tags shown on collapsed header so providers see scope at a glance */}
                          {job.addOns?.length > 0 && (
                            <div className="booking-card-addons">
                              {job.addOns.map(a => (
                                <span key={a.id} className="payment-addon-tag">{a.label}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="job-card-header-right">
                          <span className="provider-earnings">
                            ${(getBookingAmount(job) * 0.65 / 100).toFixed(2)} earned
                          </span>
                          <span className="job-expand-toggle">
                            {isExpanded ? 'Hide Details ▲' : 'View Details ▼'}
                          </span>
                        </div>
                      </div>

                      {/* Expanded details */}
                      {isExpanded && (
                        <div className="job-card-details">
                          <div className="job-detail-row">
                            <span className="job-detail-label">Cemetery</span>
                            <span className="job-detail-value">{capitalize(job.cemeteryName)}</span>
                          </div>
                          <div className="job-detail-row">
                            <span className="job-detail-label">Visit Date</span>
                            <span className="job-detail-value">{formatDate(job.preferredDate)}</span>
                          </div>
                          <div className="job-detail-row">
                            <span className="job-detail-label">Service</span>
                            <span className="job-detail-value">
                              Headstone Cleaning
                              {job.addOns?.length > 0 && ` + ${job.addOns.map(a => a.label).join(', ')}`}
                            </span>
                          </div>
                          {job.graveSection && (
                            <div className="job-detail-row">
                              <span className="job-detail-label">Section/Row</span>
                              <span className="job-detail-value">{job.graveSection}</span>
                            </div>
                          )}
                          {job.specialInstructions && (
                            <div className="job-detail-row">
                              <span className="job-detail-label">Instructions</span>
                              <span className="job-detail-value">{job.specialInstructions}</span>
                            </div>
                          )}
                          <div className="job-card-actions">
                            <button
                              className="btn-primary"
                              style={{ flex: 1 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                // Always show supply confirmation before claiming
                                setClaimConfirmJob(job);
                              }}
                            >
                              Claim Job — Earn ${(getBookingAmount(job) * 0.65 / 100).toFixed(2)}
                            </button>
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${job.cemeteryName} ${job.cemeteryCity} ${job.cemeteryState}`)}`}
                              target="_blank"
                              rel="noreferrer"
                              className="btn-directions"
                              onClick={(e) => e.stopPropagation()}
                            >
                              📍 Directions
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">🔍</div>
                <h3>No jobs available in your area yet</h3>
                <p>Check back soon!</p>
              </div>
            )}
          </div>
        )}

        {/* ========== MY JOBS TAB ========== */}
        {activeTab === 'myjobs' && (
          <div>
            <div className="dashboard-header">
              <div>
                <h2>My Jobs</h2>
                <p className="dashboard-subtitle">Jobs you have claimed</p>
              </div>
            </div>

            {myJobs.length > 0 ? (
              <div className="bookings-list">
                {myJobs.map(job => (
                  <div key={job.id} className="booking-card-wrapper provider-job-card">
                    <div className="booking-card">
                      <div className="booking-card-left">
                        <span className="booking-badge">Grave Visit</span>
                        <h3>{job.lovedOneName}</h3>
                        <p>{capitalize(job.cemeteryName)}</p>
                        <p>{capitalize(job.cemeteryCity)}, {job.cemeteryState?.toUpperCase()}</p>
                        <p className="booking-date">Visit date: {formatDate(job.preferredDate)}</p>
                        {job.graveSection && (
                          <p className="job-instructions">Section/Row: {job.graveSection}</p>
                        )}
                        {job.specialInstructions && (
                          <p className="job-instructions">Note: {job.specialInstructions}</p>
                        )}
                        {job.addOns?.length > 0 && (
                          <div className="booking-card-addons">
                            {job.addOns.map(a => (
                              <span key={a.id} className="payment-addon-tag">{a.label}</span>
                            ))}
                          </div>
                        )}
                        {/* Receipt reminder — only shown when job has add-ons that require purchasing materials */}
                        {job.status !== 'completed' && job.addOns?.some(a => SUPPLY_REQUIREMENTS[a.id]?.purchase) && (
                          <div className="receipt-reminder-banner">
                            📎 Remember to upload receipt photos for purchased materials at job completion
                          </div>
                        )}
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${job.cemeteryName} ${job.cemeteryCity} ${job.cemeteryState}`)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="btn-directions btn-directions-sm"
                        >
                          📍 Directions
                        </a>
                      </div>
                      <div className="booking-card-right">
                        <span className={`status-badge status-${job.status}`}>
                          {job.status}
                        </span>
                        <span className="provider-earnings">
                          ${(getBookingAmount(job) * 0.65 / 100).toFixed(2)} earned
                        </span>
                        {job.status !== 'completed' && (
                          <div className="photo-upload-section">
                            <div className="photo-upload-item">
                              <label className="photo-upload-label">
                                {job.beforePhotoUrl ? '✓ Before photo uploaded' : 'Upload before photo'}
                                <input
                                  type="file"
                                  accept="image/*"
                                  style={{ display: 'none' }}
                                  onChange={(e) => handlePhotoUpload(job.id, 'before', e.target.files[0])}
                                  disabled={uploadingJobId === job.id}
                                />
                              </label>
                            </div>
                            <div className="photo-upload-item">
                              <label className="photo-upload-label">
                                {job.afterPhotoUrl ? '✓ After photo uploaded' : 'Upload after photo'}
                                <input
                                  type="file"
                                  accept="image/*"
                                  style={{ display: 'none' }}
                                  onChange={(e) => handlePhotoUpload(job.id, 'after', e.target.files[0])}
                                  disabled={uploadingJobId === job.id}
                                />
                              </label>
                            </div>
                            {uploadingJobId === job.id && (
                              <p className="uploading-text">Uploading photo...</p>
                            )}
                          </div>
                        )}
                        {job.beforePhotoUrl && (
                          <a href={job.beforePhotoUrl} target="_blank" rel="noreferrer" className="photo-link">View before photo</a>
                        )}
                        {job.afterPhotoUrl && (
                          <a href={job.afterPhotoUrl} target="_blank" rel="noreferrer" className="photo-link">View after photo</a>
                        )}
                        {job.customerRating && (
                          <div className="job-rating-display">
                            {renderStars(job.customerRating)}
                            <span className="job-rating-label">Customer rating</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">📋</div>
                <h3>No jobs claimed yet</h3>
                <p>Browse available jobs and claim one to get started</p>
                <button className="btn-primary" onClick={() => setActiveTab('available')}>Browse Jobs</button>
              </div>
            )}
          </div>
        )}

        {/* ========== EARNINGS TAB ========== */}
        {activeTab === 'earnings' && (
          <div>
            <div className="dashboard-header">
              <div>
                <h2>Earnings</h2>
                <p className="dashboard-subtitle">Track your income from completed jobs</p>
              </div>
            </div>

            {/* Earnings summary */}
            <div className="stats-bar">
              <div className="stat-card stat-card-green">
                <div className="stat-icon">💰</div>
                <div className="stat-info">
                  <span className="stat-number">${(totalEarnings / 100).toFixed(2)}</span>
                  <span className="stat-label">Total Earned</span>
                </div>
              </div>
              <div className="stat-card stat-card-yellow">
                <div className="stat-icon">⏳</div>
                <div className="stat-info">
                  <span className="stat-number">${(pendingEarnings / 100).toFixed(2)}</span>
                  <span className="stat-label">Pending</span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">✅</div>
                <div className="stat-info">
                  <span className="stat-number">{completedJobs}</span>
                  <span className="stat-label">Jobs Paid</span>
                </div>
              </div>
            </div>

            {/* Payout notice */}
            <div className="earnings-notice">
              <div className="earnings-notice-icon">🏦</div>
              <div className="earnings-notice-text">
                <h4>Payouts coming soon</h4>
                <p>
                  We are setting up Square to pay you directly to your bank account.
                  Weekly automatic payouts and instant cash out will be available soon.
                  Your earned balance is being tracked and will be paid out when this launches.
                </p>
              </div>
            </div>

            {/* Completed jobs breakdown */}
            {myJobs.filter(j => j.status === 'completed').length > 0 ? (
              <div>
                <h3 className="earnings-section-title">Completed Jobs</h3>
                <div className="bookings-list">
                  {myJobs
                    .filter(j => j.status === 'completed')
                    .map(job => (
                      <div key={job.id} className="booking-card-wrapper">
                        <div className="booking-card">
                          <div className="booking-card-left">
                            <span className="booking-badge">Grave Visit</span>
                            <h3>{job.lovedOneName}</h3>
                            <p>{capitalize(job.cemeteryName)}</p>
                            <p className="booking-date">
                              Completed: {job.completedAt?.toDate
                                ? job.completedAt.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                                : formatDate(job.preferredDate)}
                            </p>
                          </div>
                          <div className="booking-card-right">
                            <span className="status-badge status-completed">completed</span>
                            <span className="provider-earnings">
                              ${(getBookingAmount(job) * 0.65 / 100).toFixed(2)} earned
                            </span>
                            {job.customerRating && (
                              <div className="job-rating-display">
                                {renderStars(job.customerRating)}
                                <span className="job-rating-label">Customer rating</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">💰</div>
                <h3>No earnings yet</h3>
                <p>Complete your first job to start earning</p>
                <button className="btn-primary" onClick={() => setActiveTab('available')}>Browse Jobs</button>
              </div>
            )}
          </div>
        )}

        {/* ========== MESSAGES TAB ========== */}
        {activeTab === 'messages' && (
          <div>
            <div className="dashboard-header">
              <div>
                <h2>Messages</h2>
                <p className="dashboard-subtitle">Chat with customers about their visits</p>
              </div>
            </div>
            <Messages userRole="provider" />
          </div>
        )}

        {/* ========== MY PROFILE TAB ========== */}
        {activeTab === 'profile' && (
          <div>
            <div className="dashboard-header">
              <div>
                <h2>My Profile</h2>
                <p className="dashboard-subtitle">Manage your caretaker account</p>
              </div>
            </div>

            {/* Independent contractor notice */}
            <div className="ic-notice">
              <span className="ic-notice-icon">ℹ️</span>
              <span>You are operating as an independent contractor on the Evermark Memorial platform. You are responsible for your own supplies, transportation, and taxes on earnings.</span>
            </div>

            {/* ——— Section 1: Personal Info ——— */}
            <div className="profile-section">
              <h3 className="profile-section-title">Personal Info</h3>

            {/* Profile card with clickable photo */}
            <div className="profile-card">
              <div className="profile-photo-container">

                {/* Clicking the photo opens the file picker
                    Camera icon overlay appears on hover */}
                <label className="profile-photo-clickable">
                  {providerData?.photoUrl ? (
                    <img
                      src={providerData.photoUrl}
                      alt="Profile"
                      className="profile-photo"
                    />
                  ) : (
                    <div className="profile-avatar">
                      {providerData?.firstName?.charAt(0)}{providerData?.lastName?.charAt(0)}
                    </div>
                  )}
                  {/* Camera overlay shown on hover */}
                  <div className="profile-photo-overlay">
                    {uploadingPhoto ? '⏳' : '📷'}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleProfilePhotoUpload}
                    disabled={uploadingPhoto}
                  />
                </label>

                {/* Small hint text below photo */}
                <p style={{ fontSize: '11px', color: '#aaa', marginTop: '4px' }}>
                  {uploadingPhoto ? 'Uploading...' : 'Click to change'}
                </p>

              </div>

              <div className="profile-info">
                <h3>{providerData?.firstName} {providerData?.lastName}</h3>
                <p>{providerData?.email}</p>
                <span className="profile-role-badge">Caretaker</span>

                {/* Rating and completed jobs summary */}
                <div className="profile-stats-mini">
                  {providerData?.rating > 0 && (
                    <span>
                      ⭐ {providerData.rating} ({providerData.ratingCount} review{providerData.ratingCount !== 1 ? 's' : ''})
                    </span>
                  )}
                  {completedJobs > 0 && (
                    <span>✅ {completedJobs} job{completedJobs > 1 ? 's' : ''} completed</span>
                  )}
                </div>
              </div>
            </div>

            {/* Profile details — editable fields show inputs when editingProfile is true */}
            <div className="profile-details">
              <div className="profile-detail-row">
                <span className="profile-detail-label">First Name</span>
                {editingProfile ? (
                  <input
                    className="profile-edit-input"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    maxLength={100}
                  />
                ) : (
                  <span className="profile-detail-value">{providerData?.firstName}</span>
                )}
              </div>
              <div className="profile-detail-row">
                <span className="profile-detail-label">Last Name</span>
                {editingProfile ? (
                  <input
                    className="profile-edit-input"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    maxLength={100}
                  />
                ) : (
                  <span className="profile-detail-value">{providerData?.lastName}</span>
                )}
              </div>
              <div className="profile-detail-row">
                <span className="profile-detail-label">Email</span>
                <span className="profile-detail-value">{providerData?.email}</span>
              </div>
              <div className="profile-detail-row">
                <span className="profile-detail-label">Phone</span>
                {editingProfile ? (
                  <input
                    className="profile-edit-input"
                    value={editPhone}
                    onChange={(e) => setEditPhone(e.target.value)}
                    placeholder="e.g. 412-555-0123"
                    maxLength={20}
                  />
                ) : (
                  <span className="profile-detail-value">{providerData?.phone || 'Not provided'}</span>
                )}
              </div>
              <div className="profile-detail-row">
                <span className="profile-detail-label">Service City</span>
                {editingProfile ? (
                  <input
                    className="profile-edit-input"
                    value={editServiceCity}
                    onChange={(e) => setEditServiceCity(e.target.value)}
                    placeholder="e.g. Pittsburgh"
                    maxLength={100}
                  />
                ) : (
                  <span className="profile-detail-value">{capitalize(providerData?.serviceCity)}</span>
                )}
              </div>
              <div className="profile-detail-row">
                <span className="profile-detail-label">Service State</span>
                {editingProfile ? (
                  <select
                    className="booking-select profile-edit-select"
                    value={editServiceState}
                    onChange={(e) => setEditServiceState(e.target.value)}
                  >
                    <option value="">Select state</option>
                    <option value="al">Alabama</option>
                    <option value="ak">Alaska</option>
                    <option value="az">Arizona</option>
                    <option value="ar">Arkansas</option>
                    <option value="ca">California</option>
                    <option value="co">Colorado</option>
                    <option value="ct">Connecticut</option>
                    <option value="de">Delaware</option>
                    <option value="fl">Florida</option>
                    <option value="ga">Georgia</option>
                    <option value="hi">Hawaii</option>
                    <option value="id">Idaho</option>
                    <option value="il">Illinois</option>
                    <option value="in">Indiana</option>
                    <option value="ia">Iowa</option>
                    <option value="ks">Kansas</option>
                    <option value="ky">Kentucky</option>
                    <option value="la">Louisiana</option>
                    <option value="me">Maine</option>
                    <option value="md">Maryland</option>
                    <option value="ma">Massachusetts</option>
                    <option value="mi">Michigan</option>
                    <option value="mn">Minnesota</option>
                    <option value="ms">Mississippi</option>
                    <option value="mo">Missouri</option>
                    <option value="mt">Montana</option>
                    <option value="ne">Nebraska</option>
                    <option value="nv">Nevada</option>
                    <option value="nh">New Hampshire</option>
                    <option value="nj">New Jersey</option>
                    <option value="nm">New Mexico</option>
                    <option value="ny">New York</option>
                    <option value="nc">North Carolina</option>
                    <option value="nd">North Dakota</option>
                    <option value="oh">Ohio</option>
                    <option value="ok">Oklahoma</option>
                    <option value="or">Oregon</option>
                    <option value="pa">Pennsylvania</option>
                    <option value="ri">Rhode Island</option>
                    <option value="sc">South Carolina</option>
                    <option value="sd">South Dakota</option>
                    <option value="tn">Tennessee</option>
                    <option value="tx">Texas</option>
                    <option value="ut">Utah</option>
                    <option value="vt">Vermont</option>
                    <option value="va">Virginia</option>
                    <option value="wa">Washington</option>
                    <option value="wv">West Virginia</option>
                    <option value="wi">Wisconsin</option>
                    <option value="wy">Wyoming</option>
                  </select>
                ) : (
                  <span className="profile-detail-value">{providerData?.serviceState?.toUpperCase()}</span>
                )}
              </div>
              <div className="profile-detail-row">
                <span className="profile-detail-label">Jobs Completed</span>
                <span className="profile-detail-value">{completedJobs}</span>
              </div>
              <div className="profile-detail-row">
                <span className="profile-detail-label">Total Earned</span>
                <span className="profile-detail-value">${(totalEarnings / 100).toFixed(2)}</span>
              </div>
              <div className="profile-detail-row">
                <span className="profile-detail-label">Average Rating</span>
                <span className="profile-detail-value">
                  {providerData?.rating > 0
                    ? `⭐ ${providerData.rating} / 5 (${providerData.ratingCount} review${providerData.ratingCount !== 1 ? 's' : ''})`
                    : 'No ratings yet'}
                </span>
              </div>

              {/* Edit Profile / Save / Cancel buttons */}
              {editingProfile ? (
                <div className="profile-save-row">
                  <button
                    className="btn-primary"
                    onClick={handleSaveProfile}
                    disabled={profileSaving}
                  >
                    {profileSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    className="btn-outline"
                    onClick={() => {
                      setEditFirstName(providerData?.firstName || '');
                      setEditLastName(providerData?.lastName || '');
                      setEditPhone(providerData?.phone || '');
                      setEditServiceCity(providerData?.serviceCity || '');
                      setEditServiceState(providerData?.serviceState || '');
                      setEditingProfile(false);
                      setProfileError('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="profile-save-row">
                  <button className="btn-edit-inline" onClick={() => setEditingProfile(true)}>
                    Edit Profile
                  </button>
                  <button
                    className="btn-outline"
                    style={{ fontSize: '13px' }}
                    onClick={() => { setShowChangePw(true); setChangePwSuccess(false); setChangePwError(''); }}
                  >
                    Change Password
                  </button>
                </div>
              )}
              {profileError && <p className="profile-error-msg">{profileError}</p>}
              {profileSuccess && <p className="profile-success-msg">Profile updated successfully.</p>}
            </div>

            {/* Bio section — separate card below profile details */}
            <div className="bio-section">
              <div className="bio-section-header">
                <h4>About Me</h4>
                {!editingBio && (
                  <button className="btn-edit-inline" onClick={() => setEditingBio(true)}>Edit</button>
                )}
              </div>
              {editingBio ? (
                <>
                  <textarea
                    className="booking-textarea"
                    value={bioValue}
                    onChange={(e) => { if (e.target.value.length <= 300) setBioValue(e.target.value); }}
                    rows={4}
                    maxLength={300}
                    placeholder="Tell customers about your experience and approach..."
                  />
                  <p className="bio-char-count">{bioValue.length}/300</p>
                  <div className="bio-edit-actions">
                    <button className="btn-primary" onClick={handleSaveBio}>Save Bio</button>
                    <button className="btn-outline" onClick={() => { setBioValue(providerData?.bio || ''); setEditingBio(false); }}>Cancel</button>
                  </div>
                </>
              ) : (
                <p className="bio-display">
                  {providerData?.bio || <span className="bio-empty">No bio added yet. Click Edit to add one.</span>}
                </p>
              )}
            </div>

          </div>

          </div>
        )}

      </div>

      {/* ========== CHANGE PASSWORD MODAL ========== */}
      {showChangePw && (
        <div className="modal-overlay" onClick={() => { setShowChangePw(false); setChangePwError(''); setChangePwSuccess(false); }}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowChangePw(false)}>×</button>
            <h3 className="modal-title">Change Password</h3>
            {changePwSuccess ? (
              <p style={{ color: '#4a7c59', textAlign: 'center', padding: '16px 0' }}>Password updated successfully.</p>
            ) : (
              <>
                <input
                  className="auth-input"
                  type="password"
                  placeholder="Current password"
                  value={changePwForm.current}
                  onChange={e => setChangePwForm(f => ({ ...f, current: e.target.value }))}
                  style={{ marginBottom: '10px' }}
                />
                <input
                  className="auth-input"
                  type="password"
                  placeholder="New password"
                  value={changePwForm.next}
                  onChange={e => setChangePwForm(f => ({ ...f, next: e.target.value }))}
                  style={{ marginBottom: '6px' }}
                />
                {changePwForm.next && (
                  <div className="pw-strength" style={{ marginBottom: '10px' }}>
                    {[
                      { key: 'length', label: 'At least 8 characters' },
                      { key: 'upper', label: 'One uppercase letter' },
                      { key: 'number', label: 'One number' },
                      { key: 'special', label: 'One special character' },
                    ].map(({ key, label }) => {
                      const pass = pwChecks(changePwForm.next)[key];
                      return (
                        <div key={key} className={`pw-check ${pass ? 'pw-check-pass' : 'pw-check-fail'}`}>
                          <span>{pass ? '✓' : '✗'}</span> {label}
                        </div>
                      );
                    })}
                  </div>
                )}
                <input
                  className="auth-input"
                  type="password"
                  placeholder="Confirm new password"
                  value={changePwForm.confirm}
                  onChange={e => setChangePwForm(f => ({ ...f, confirm: e.target.value }))}
                  style={{ marginBottom: '10px' }}
                />
                {changePwError && <p className="auth-error" style={{ marginBottom: '10px' }}>{changePwError}</p>}
                <button
                  className="btn-primary"
                  style={{ width: '100%' }}
                  onClick={handleChangePassword}
                  disabled={changePwLoading}
                >
                  {changePwLoading ? 'Updating…' : 'Update Password'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ========== CLAIM CONFIRMATION MODAL ========== */}
      {claimConfirmJob && (
        <div className="modal-overlay" onClick={() => setClaimConfirmJob(null)}>
          <div className="claim-confirm-modal" onClick={(e) => e.stopPropagation()}>

            <h3 className="claim-confirm-title">Job Details — Are you ready?</h3>

            {/* Job summary */}
            <div className="claim-confirm-summary">
              <p><strong>{claimConfirmJob.lovedOneName}</strong></p>
              <p>{capitalize(claimConfirmJob.cemeteryName)}, {capitalize(claimConfirmJob.cemeteryCity)} {claimConfirmJob.cemeteryState?.toUpperCase()}</p>
              <p>Preferred date: {formatDate(claimConfirmJob.preferredDate)}</p>
            </div>

            {/* What's included — base always first, then each add-on */}
            <div className="claim-confirm-section">
              <p className="claim-confirm-section-title">What's included in this job</p>

              {/* Base visit supplies */}
              <div className="claim-supply-item">
                <p className="claim-supply-label">{SUPPLY_REQUIREMENTS.base.label}</p>
                <ul className="claim-supply-list">
                  {SUPPLY_REQUIREMENTS.base.supplies.map(s => <li key={s}>{s}</li>)}
                </ul>
              </div>

              {/* Per add-on supplies */}
              {claimConfirmJob.addOns?.map(addOn => {
                const req = SUPPLY_REQUIREMENTS[addOn.id];
                if (!req) return null;
                return (
                  <div key={addOn.id} className={`claim-supply-item ${req.purchase ? 'claim-supply-purchase' : ''}`}>
                    <p className="claim-supply-label">
                      {req.label}
                      {req.purchase && <span className="claim-purchase-badge">Purchase required</span>}
                    </p>
                    {req.supplies.length > 0 && (
                      <ul className="claim-supply-list">
                        {req.supplies.map(s => <li key={s}>{s}</li>)}
                      </ul>
                    )}
                    {req.purchase && (
                      <p className="claim-purchase-note">
                        Est. budget: <strong>${req.budget}</strong> — {req.note}
                      </p>
                    )}
                    {!req.purchase && req.note && (
                      <p className="claim-supply-note">{req.note}</p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Earnings breakdown */}
            <div className="claim-confirm-section">
              <p className="claim-confirm-section-title">Your earnings</p>
              <div className="claim-earnings-rows">
                <div className="claim-earnings-row">
                  <span>Base Visit</span>
                  <span>${(59 * 0.65).toFixed(2)}</span>
                </div>
                {claimConfirmJob.addOns?.map(addOn => (
                  <div key={addOn.id} className="claim-earnings-row">
                    <span>{addOn.label}</span>
                    <span>${(addOn.price * 0.65).toFixed(2)}</span>
                  </div>
                ))}
                <div className="claim-earnings-row claim-earnings-total">
                  <span>Total earnings</span>
                  <span>${(getBookingAmount(claimConfirmJob) * 0.65 / 100).toFixed(2)}</span>
                </div>
              </div>
              {/* Amber reimbursement note — only shown when job has purchasable add-ons */}
              {claimConfirmJob.addOns?.some(a => SUPPLY_REQUIREMENTS[a.id]?.purchase) && (
                <p className="claim-reimbursement-note">
                  ⚠️ Material costs for purchased items will be reimbursed through your payout. Keep your receipts and upload photos at job completion.
                </p>
              )}
            </div>

            {/* Action buttons */}
            <div className="claim-confirm-actions">
              <button
                className="btn-outline"
                onClick={() => setClaimConfirmJob(null)}
              >
                Not Now
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  const jobId = claimConfirmJob.id;
                  setClaimConfirmJob(null);
                  handleClaimJob(jobId);
                }}
              >
                I'm Ready — Claim Job
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}

export default ProviderDashboard;